import { useEffect, useRef, useState } from "react";
import { Platform, Linking } from "react-native";
import axios from "axios";
import { determineBaseOrigin, isFromAndroidWebview, isIframeCheck, REPAYMENT_COLLECTION_TYPE } from "@app/utils";
import ROUTE from "@app/navigationApp/routes";
import { endpoints } from "@app/api/apis";
import BackgroundTimer from "react-native-background-timer";
import InAppBrowser from "react-native-inappbrowser-reborn";

interface TataPaymentParams {
    selectedAmount: number;
    creditId: string;
    paymentType: string;
    isDecreaseForeclosureFlow: boolean;
    navigation: any;
    setOpenInProgressModal: (isOpen: boolean) => void;
}

const useTataPayment = () => {
    const [loading, setLoading] = useState(false);
    let polling = useRef<NodeJS.Timeout | null>(null);
    const startTataPayment = async ({
        selectedAmount,
        creditId,
        paymentType,
        isDecreaseForeclosureFlow,
        navigation,
        setOpenInProgressModal
    }: TataPaymentParams): Promise<void> => {
        setLoading(true);
        const payload = {
            baseOriginUrl: determineBaseOrigin(),
            collectionAmount: `${selectedAmount}`,
            creditId: creditId,
            paymentGatewayType: null,
            redirect: Platform.OS === 'web' && !isIframeCheck() && !isFromAndroidWebview(),
            collectionType: paymentType || `${REPAYMENT_COLLECTION_TYPE.PRINCIPAL_REPAYMENT}`,
            isDecreaseForeclosureFlow: isDecreaseForeclosureFlow
        };

        try {
            const response = await axios.post(endpoints.paymentInit, payload);
            let popupWindow: Window | null = null;

            if (response?.status === 200) {
                const paymentLink = response?.data?.paymentLink;
                const collectionId = response?.data?.collectionId;

                if (Platform.OS === 'web') {
                    if (isIframeCheck() || isFromAndroidWebview()) {
                        setOpenInProgressModal(true);
                        const width = 1002;
                        const height = 704;
                        const left = (window.innerWidth - width) / 2;
                        const top = (window.innerHeight - height) / 2;

                        popupWindow = isFromAndroidWebview()
                            ? window.open(paymentLink, "_blank")
                            : window.open(paymentLink, "_blank", `width=${width}, height=${height}, top=${top}, left=${left}`);
                    } else {
                        window.open(paymentLink, "_self");
                    }
                } else {
                    if (Platform.OS === 'android') {
                        navigation.navigate(ROUTE.PAYMENT_WEBVIEW, {
                            uri: paymentLink,
                            collectionId,
                            doFormPost: false
                        });
                    } else {
                        const pollingUrl = endpoints.paymentPolling + collectionId;
                        await openLink(paymentLink,pollingUrl,navigation,collectionId);
                    }
                    if(Platform.OS === 'android'){
                    const pollingUrl = endpoints.paymentPolling + collectionId;
                    BackgroundTimer.runBackgroundTimer(async () => {
                        axios.get(pollingUrl)
                            .then((pollingResponse) => {
                                if (pollingResponse.data.collectionStatus !== 'REQUESTED') {
                                    InAppBrowser.close();
                                    BackgroundTimer.stopBackgroundTimer();
                                    navigation.navigate(ROUTE.PAYMENT_STATUS, { collectionId });
                                }
                            });
                        }, 3000);
                    }
                }

                if (collectionId) {
                    polling.current = setInterval(() => {
                        pollTataPayment(collectionId, navigation, popupWindow,setOpenInProgressModal);
                    }, 5000);
                    // Clear polling after 3 minutes (180000 ms)
                    setTimeout(() => {
                        if (polling.current) {
                            clearInterval(polling.current);
                            polling.current = null;
                        }
                    }, 180000);
                }
            }
        } catch (error) {
            console.error('Tata Payment Error:', error);
        } finally {
            setLoading(false);
        }
    };

    

    const openLink = async (url: string,pollingUrl: string,navigation: any,collectionId: string) => {
        try {
            if (await InAppBrowser.isAvailable()) {
                BackgroundTimer.runBackgroundTimer(async () => {
                    const response = await axios.get(pollingUrl);
                    if (response.data.collectionStatus !== 'REQUESTED') {
                        InAppBrowser.close();
                        BackgroundTimer.stopBackgroundTimer();
                        navigation.navigate(ROUTE.PAYMENT_STATUS, { collectionId });
                    }
                }, 3000);
                const result = await InAppBrowser.open(url, {
                    dismissButtonStyle: 'cancel',
                    preferredBarTintColor: '#453AA4',
                    preferredControlTintColor: 'white',
                    readerMode: false,
                    animated: true,
                    modalPresentationStyle: 'fullScreen',
                    modalTransitionStyle: 'coverVertical',
                    modalEnabled: true,
                    enableBarCollapsing: false,
                    showInRecents: true,
                    showTitle: true,
                    toolbarColor: '#1434CB',
                    secondaryToolbarColor: 'black',
                    navigationBarColor: 'black',
                    navigationBarDividerColor: 'white',
                    enableUrlBarHiding: true,
                    enableDefaultShare: true,
                    forceCloseOnRedirection: false,
                    animations: {
                        startEnter: 'slide_in_right',
                        startExit: 'slide_out_left',
                        endEnter: 'slide_in_left',
                        endExit: 'slide_out_right'
                    },
                    headers: {
                        'my-custom-header': 'my custom header value'
                    }
                });

                if (result.type === "cancel") {
                    BackgroundTimer?.stopBackgroundTimer();
                }
            } else {
                Linking.openURL(url);
            }
        } catch (error) {
            console.error('Error opening link:', error);
        }
    };

    const stopPolling = () => {
        if (polling.current) {
            clearInterval(polling.current);
            polling.current = null; // Reset polling to null after clearing
        }
    };

    const pollTataPayment = (
        collectionId: string,
        navigation: any,
        popupWindow: Window | null,
        setOpenInProgressModal: (isOpen: boolean) => void,
    ): void => {
        const url = endpoints.paymentPolling + collectionId;
        axios.get(url)
            .then((response) => {
                setOpenInProgressModal(false);
                if (popupWindow?.closed) {
                    stopPolling()
                } else {
                    if (response?.data?.collectionStatus !== 'REQUESTED') {
                        stopPolling()
                        if (popupWindow) {
                            popupWindow.close();
                        }
                        navigation.navigate(ROUTE.PAYMENT_STATUS, { collectionId: response.data.collectionId });
                    }
                }
            })
            .catch((error) => {
                console.error('Tata Payment Polling Error:', error);
            });
    };
    useEffect(() => {
        return () => {
            stopPolling(); // Clear polling on component unmount
        };
    }, []);

    return { startTataPayment, loading };
};

export default useTataPayment;
